import { createContext, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useActiveFamily } from './ActiveFamilyContext';

interface Member {
  id: string;
  name: string;
  [key: string]: any;
}

interface Checklist {
  id: string;
  name: string;
  [key: string]: any;
}

interface Activity {
  id: string;
  name: string;
  icon?: string;
  [key: string]: any;
}

interface FamilyRealtimeStaticData {
  familyId: string | null;
  members: Member[];
  checklists: Checklist[];
  activities: Activity[];
  checklistNameMap: Record<string, string>;
  activityNameMap: Record<string, string>;
  activityIconMap: Record<string, string>;
  loading: boolean;
}

const FamilyRealtimeDataContext = createContext<FamilyRealtimeStaticData | undefined>(undefined);

export function FamilyRealtimeDataProvider({ children }: { children: React.ReactNode }) {
  const { activeFamily } = useActiveFamily();
  const familyId = activeFamily?.id ?? null;

  const [members, setMembers] = useState<Member[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    if (!familyId) {
      setMembers([]);
      setChecklists([]);
      setActivities([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubs: Array<() => void> = [];

    unsubs.push(
      onSnapshot(
        query(collection(db, 'members'), where('familyId', '==', familyId)),
        (snap) => {
          if (!isMountedRef.current) return;
          setMembers(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        },
        () => setMembers([])
      )
    );

    unsubs.push(
      onSnapshot(
        query(collection(db, 'checklist'), where('familyId', '==', familyId)),
        (snap) => {
          if (!isMountedRef.current) return;
          setChecklists(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        },
        () => setChecklists([])
      )
    );

    unsubs.push(
      onSnapshot(
        query(collection(db, 'activities'), where('familyId', '==', familyId)),
        (snap) => {
          if (!isMountedRef.current) return;
          setActivities(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        },
        () => setActivities([])
      )
    );

    setLoading(false);

    return () => {
      isMountedRef.current = false;
      unsubs.forEach((fn) => {
        try {
          fn();
        } catch (e) {
          // Ignore errors when listener is already closed
        }
      });
    };
  }, [familyId]);

  const checklistNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    checklists.forEach((item: any) => {
      map[item.id] = item.name || item.title || item.summary || item.id;
    });
    return map;
  }, [checklists]);

  const activityNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    activities.forEach((item: any) => {
      map[item.id] = item.name || item.title || item.summary || item.id;
    });
    return map;
  }, [activities]);

  const activityIconMap = useMemo(() => {
    const map: Record<string, string> = {};
    activities.forEach((item: any) => {
      map[item.id] = item.icon || '⚡';
    });
    return map;
  }, [activities]);

  const value = useMemo<FamilyRealtimeStaticData>(
    () => ({
      familyId,
      members,
      checklists,
      activities,
      checklistNameMap,
      activityNameMap,
      activityIconMap,
      loading,
    }),
    [familyId, members, checklists, activities, checklistNameMap, activityNameMap, activityIconMap, loading]
  );

  return <FamilyRealtimeDataContext.Provider value={value}>{children}</FamilyRealtimeDataContext.Provider>;
}

export function useFamilyRealtimeStaticData(): Omit<FamilyRealtimeStaticData, 'familyId'> {
  const ctx = useContext(FamilyRealtimeDataContext);
  if (!ctx) {
    throw new Error('useFamilyRealtimeStaticData must be used within a FamilyRealtimeDataProvider');
  }

  const { familyId: _familyId, ...rest } = ctx;
  return rest;
}

export function useFamilyRealtimeStaticDataWithFamilyId(): FamilyRealtimeStaticData {
  const ctx = useContext(FamilyRealtimeDataContext);
  if (!ctx) {
    throw new Error('useFamilyRealtimeStaticDataWithFamilyId must be used within a FamilyRealtimeDataProvider');
  }

  return ctx;
}
